import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Clock, User, Mail, Calendar as CalendarIcon, Loader2, RefreshCw, Send, Edit3, Save, X, Ban, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ScheduledEmail, useEmailTemplateByKey, useCancelScheduledEmail } from '@/hooks/useEmailLogs';
import { useResendScheduledEmail, useUpdateScheduledEmailVariables } from '@/hooks/useResendScheduledEmail';
import { useSendScheduledEmailNow, useRescheduleEmail } from '@/hooks/useEmailSystemHealth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

interface ScheduledEmailPreviewModalProps {
  email: ScheduledEmail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onResend?: () => void;
}

// Placeholder avatar as base64 SVG (silhouette icon)
const PLACEHOLDER_AVATAR_BASE64 = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgdmlld0JveD0iMCAwIDgwIDgwIj48cmVjdCBmaWxsPSIjMjIyIiB3aWR0aD0iODAiIGhlaWdodD0iODAiIHJ4PSI4Ii8+PGNpcmNsZSBjeD0iNDAiIGN5PSIyOCIgcj0iMTQiIGZpbGw9IiM0NDQiLz48cGF0aCBkPSJNMTUgNzVjMC0xNCAxMS0yNSAyNS0yNXMyNSAxMSAyNSAyNSIgZmlsbD0iIzQ0NCIvPjwvc3ZnPg==';

// Photo variable keys that need base64 conversion
const PHOTO_VARIABLES = ['LikerPhoto', 'SenderPhoto', 'UserPhoto', 'MatchPhoto', 'Photo'];

/**
 * Convert a photo path to base64 data URI
 */
const convertPhotoToBase64 = async (photoPath: string): Promise<string> => {
  // If already base64 or empty, return as-is
  if (!photoPath || photoPath.startsWith('data:')) {
    return photoPath || PLACEHOLDER_AVATAR_BASE64;
  }
  
  // If it's a full URL that's not from our storage, use placeholder
  if (photoPath.startsWith('http') && !photoPath.includes('supabase')) {
    return PLACEHOLDER_AVATAR_BASE64;
  }

  try {
    // Extract relative path if it's a full Supabase URL
    let relativePath = photoPath;
    if (photoPath.includes('/storage/v1/object/')) {
      const match = photoPath.match(/\/storage\/v1\/object\/(?:public|sign)\/([^?]+)/);
      if (match) {
        relativePath = match[1];
      }
    }

    // Determine bucket from path
    let bucket = 'profile_photos';
    let filePath = relativePath;
    
    if (relativePath.includes('/')) {
      const parts = relativePath.split('/');
      if (['profile_photos', 'chat_media', 'verification_photos'].includes(parts[0])) {
        bucket = parts[0];
        filePath = parts.slice(1).join('/');
      }
    }

    // Download the file
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(filePath);

    if (error || !data) {
      console.warn('Failed to download photo:', photoPath, error);
      return PLACEHOLDER_AVATAR_BASE64;
    }

    // Convert Blob to base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        resolve(PLACEHOLDER_AVATAR_BASE64);
      };
      reader.readAsDataURL(data);
    });
  } catch (err) {
    console.error('Error converting photo to base64:', err);
    return PLACEHOLDER_AVATAR_BASE64;
  }
};

/**
 * Replace template variables supporting both {{ .Variable }} and {{ Variable }} syntax
 */
const replaceVariables = (html: string, variables: Record<string, string>): string => {
  let result = html;
  
  Object.entries(variables).forEach(([key, value]) => {
    // Replace {{ .Variable }} format (with dot)
    const regexWithDot = new RegExp(`\\{\\{\\s*\\.${key}\\s*\\}\\}`, 'g');
    result = result.replace(regexWithDot, value || '');
    
    // Replace {{ Variable }} format (without dot)
    const regexWithoutDot = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
    result = result.replace(regexWithoutDot, value || '');
  });
  
  return result;
};

const ScheduledEmailPreviewModal = ({ email, open, onOpenChange, onResend }: ScheduledEmailPreviewModalProps) => {
  const { data: template, isLoading, refetch: refetchTemplate } = useEmailTemplateByKey(email?.template_key || null);
  const resendEmail = useResendScheduledEmail();
  const updateVariables = useUpdateScheduledEmailVariables();
  const cancelEmail = useCancelScheduledEmail();
  const sendNow = useSendScheduledEmailNow();
  const rescheduleEmail = useRescheduleEmail();
  
  const [isEditingVariables, setIsEditingVariables] = useState(false);
  const [editedVariables, setEditedVariables] = useState<Record<string, string>>({});
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(undefined);
  const [rescheduleTime, setRescheduleTime] = useState('12:00');
  const [processedVariables, setProcessedVariables] = useState<Record<string, string>>({});
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);

  // Initialize edited variables when email changes
  useEffect(() => {
    if (email?.variables) {
      setEditedVariables(email.variables as Record<string, string>);
    }
  }, [email]);

  // Process photo variables to base64 when variables change
  useEffect(() => {
    const processPhotos = async () => {
      const variables = isEditingVariables ? editedVariables : (email?.variables as Record<string, string>) || {};
      
      if (Object.keys(variables).length === 0) {
        setProcessedVariables({});
        return;
      }

      setIsProcessingPhotos(true);
      
      try {
        const processed = { ...variables };
        
        // Process each photo variable
        for (const photoKey of PHOTO_VARIABLES) {
          if (photoKey in processed) {
            processed[photoKey] = await convertPhotoToBase64(processed[photoKey]);
          }
        }
        
        setProcessedVariables(processed);
      } catch (err) {
        console.error('Error processing photos:', err);
        setProcessedVariables(variables);
      } finally {
        setIsProcessingPhotos(false);
      }
    };

    processPhotos();
  }, [email?.variables, editedVariables, isEditingVariables]);

  const renderedHtml = useMemo(() => {
    if (!template?.html_content) return null;
    if (Object.keys(processedVariables).length === 0 && email?.variables) return null;

    return replaceVariables(template.html_content, processedVariables);
  }, [template, processedVariables, email?.variables]);

  const handleRefreshPreview = async () => {
    await refetchTemplate();
  };

  const handleResend = async () => {
    if (!email) return;
    
    await resendEmail.mutateAsync({
      emailId: email.id,
      templateKey: email.template_key,
      toEmail: email.to_email,
      userId: email.user_id,
      variables: isEditingVariables ? editedVariables : (email.variables as Record<string, string>),
    });
    
    onResend?.();
  };

  const handleSaveVariables = async () => {
    if (!email) return;
    
    await updateVariables.mutateAsync({
      emailId: email.id,
      variables: editedVariables,
    });
    
    setIsEditingVariables(false);
  };

  const handleCancelEdit = () => {
    setEditedVariables(email?.variables as Record<string, string> || {});
    setIsEditingVariables(false);
  };

  const handleSendNow = async () => {
    if (!email) return;
    try {
      await sendNow.mutateAsync(email.id);
      onResend?.();
      onOpenChange(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleCancelEmail = async () => {
    if (!email) return;
    try {
      await cancelEmail.mutateAsync(email.id);
      toast.success('Email cancelado');
      onResend?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Erro ao cancelar email');
    }
  };

  const handleReschedule = async () => {
    if (!email || !rescheduleDate) return;
    try {
      const [hours, minutes] = rescheduleTime.split(':').map(Number);
      const newDate = new Date(rescheduleDate);
      newDate.setHours(hours, minutes, 0, 0);
      
      await rescheduleEmail.mutateAsync({
        emailId: email.id,
        newDate: newDate,
      });
      
      setIsRescheduleOpen(false);
      onResend?.();
      toast.success('Email reagendado com sucesso');
    } catch (error) {
      toast.error('Erro ao reagendar email');
    }
  };

  const isPending = email?.status === 'pending';

  if (!email) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-gray-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-luxury-pinkBright" />
            Preview do Email Agendado
          </DialogTitle>
        </DialogHeader>

        {/* Action Buttons - Primary Actions */}
        {isPending && (
          <div className="bg-white/5 rounded-lg p-3 mb-4">
            <p className="text-xs text-white/40 mb-3 font-medium">AÇÕES DO EMAIL AGENDADO</p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleSendNow}
                disabled={sendNow.isPending}
                className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white hover:opacity-90"
              >
                {sendNow.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Enviar Agora
              </Button>
              
              <Popover open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Reagendar
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4 bg-gray-900 border-white/10" align="start">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Nova Data</Label>
                      <Calendar
                        mode="single"
                        selected={rescheduleDate}
                        onSelect={setRescheduleDate}
                        disabled={(date) => date < new Date()}
                        className="rounded-md border border-white/10 bg-white/5 pointer-events-auto"
                        locale={ptBR}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white/60 text-xs">Horário</Label>
                      <Input
                        type="time"
                        value={rescheduleTime}
                        onChange={(e) => setRescheduleTime(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleReschedule}
                        disabled={!rescheduleDate || rescheduleEmail.isPending}
                        className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white hover:opacity-90 flex-1"
                      >
                        {rescheduleEmail.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Confirmar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsRescheduleOpen(false)}
                        className="text-white/60 hover:text-white"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEmail}
                disabled={cancelEmail.isPending}
                className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
              >
                {cancelEmail.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4 mr-2" />
                )}
                Cancelar Envio
              </Button>
            </div>
          </div>
        )}

        {/* Secondary Actions */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshPreview}
            disabled={isLoading}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar Preview
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditingVariables(!isEditingVariables)}
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            <Edit3 className="w-4 h-4 mr-2" />
            {isEditingVariables ? 'Ver Preview' : 'Editar Variáveis'}
          </Button>
          
          {!isPending && (
            <Button
              size="sm"
              onClick={handleResend}
              disabled={resendEmail.isPending}
              className="bg-gradient-to-r from-[#C4406B] to-[#9F3764] text-white hover:opacity-90"
            >
              {resendEmail.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Reenviar Email
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
            <User className="w-4 h-4 text-white/40" />
            <div>
              <p className="text-white/40 text-xs">Destinatário</p>
              <p className="text-white text-sm">{email.to_email}</p>
              {email.username && (
                <p className="text-white/60 text-xs">@{email.username}</p>
              )}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-white/40" />
            <div>
              <p className="text-white/40 text-xs">Agendado para</p>
              <p className="text-white text-sm">
                {format(new Date(email.scheduled_for), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Badge className="bg-luxury-pinkBright/20 text-luxury-pinkBright border-luxury-pinkBright/30">
            {email.template_key}
          </Badge>
          <Badge variant="outline" className={cn(
            "border-white/10",
            email.status === 'pending' && "bg-yellow-500/20 text-yellow-400",
            email.status === 'sent' && "bg-green-500/20 text-green-400",
            email.status === 'failed' && "bg-red-500/20 text-red-400",
            email.status === 'cancelled' && "bg-gray-500/20 text-gray-400"
          )}>
            <Clock className="w-3 h-3 mr-1" />
            {email.status}
          </Badge>
        </div>

        {/* Variables Editor */}
        {isEditingVariables && Object.keys(editedVariables).length > 0 && (
          <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-white/60 text-sm font-medium">Editar Variáveis</p>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelEdit}
                  className="text-white/60 hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveVariables}
                  disabled={updateVariables.isPending}
                  className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                >
                  {updateVariables.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Salvar
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(editedVariables).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-white/60 text-xs">{key}</Label>
                  <Input
                    value={value}
                    onChange={(e) => setEditedVariables(prev => ({ ...prev, [key]: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Variables Display (when not editing) */}
        {!isEditingVariables && email.variables && Object.keys(email.variables).length > 0 && (
          <div className="bg-white/5 rounded-lg p-3 mb-4">
            <p className="text-white/40 text-xs mb-2">Variáveis</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(email.variables as Record<string, string>).map(([key, value]) => (
                <span key={key} className="text-xs bg-white/10 px-2 py-1 rounded text-white/80">
                  <span className="text-luxury-pinkBright">{key}:</span> {String(value).substring(0, 30)}{String(value).length > 30 ? '...' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Email Preview with min-width for proper rendering */}
        <div className="flex-1 overflow-hidden rounded-lg border border-white/10">
          {isLoading || isProcessingPhotos ? (
            <div className="flex items-center justify-center h-64 bg-white/5">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-luxury-pinkBright mx-auto mb-2" />
                <p className="text-white/40 text-sm">
                  {isProcessingPhotos ? 'Processando imagens...' : 'Carregando template...'}
                </p>
              </div>
            </div>
          ) : renderedHtml ? (
            <div className="overflow-x-auto h-[400px]">
              <iframe
                srcDoc={renderedHtml}
                className="h-full bg-white rounded"
                style={{ minWidth: '600px', width: '100%' }}
                title="Email Preview"
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-white/5 text-white/40">
              Template não encontrado ou inativo
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduledEmailPreviewModal;
